import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../utils/api';

const CSS = `
  @keyframes bg-move { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
  @keyframes glow    { 0%,100%{box-shadow:0 0 24px rgba(200,0,90,.5)} 50%{box-shadow:0 0 48px rgba(200,0,90,.8)} }
  @keyframes sh-text { 0%{background-position:-200% center} 100%{background-position:200% center} }
  @keyframes spin    { to{transform:rotate(360deg)} }
  @keyframes pulse   { 0%,100%{opacity:.5} 50%{opacity:1} }
  @keyframes slide-u { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
  @keyframes slide-d { from{opacity:0;transform:translateY(-20px)} to{opacity:1;transform:none} }
  .ms-logo { background:linear-gradient(135deg,#C8005A,#9B6FD4,#00CFDB); background-size:200%; -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; animation:sh-text 3s linear infinite; }
  .ms-glow { animation:glow 2.5s ease-in-out infinite; }
  .ms-inp  { width:100%; padding:.8rem 1rem; border-radius:.75rem; background:rgba(255,255,255,.09); border:1.5px solid rgba(255,255,255,.12); color:#fff; font-size:.875rem; outline:none; box-sizing:border-box; transition:all .2s; }
  .ms-inp::placeholder { color:rgba(255,255,255,.3); }
  .ms-inp:focus { border-color:rgba(200,0,90,.7); box-shadow:0 0 0 3px rgba(200,0,90,.18); }
  .ms-btn  { width:100%; padding:.9rem; border:none; border-radius:.75rem; background:#C8005A; color:#fff; font-size:.9rem; font-weight:700; cursor:pointer; box-shadow:0 4px 28px rgba(200,0,90,.5); transition:all .2s; }
  .ms-btn:disabled { opacity:.55; cursor:not-allowed; }
`;

function genId() {
  const a = new Uint8Array(16); crypto.getRandomValues(a);
  return Array.from(a).map(b=>b.toString(16).padStart(2,'0')).join('');
}

const LS_KEY = 'ms-tg-pending';

export default function Login() {
  const { login }  = useAuth();
  const { theme }  = useTheme();
  const navigate   = useNavigate();

  const [form,    setForm]    = useState({ username:'', password:'' });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [blocked, setBlocked] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [waiting,  setWaiting]  = useState(null);
  const [forgot,   setForgot]   = useState(false);
  const [fLogin,   setFLogin]   = useState('');
  const [fSent,    setFSent]    = useState(false);
  const [fLoading, setFLoading] = useState(false);
  const [fError,   setFError]   = useState('');
  const pollRef = useRef(null);

  // При возврате из Telegram — проверяем localStorage
  useEffect(() => {
    const check = () => {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      try {
        const { sessionId, botUrl, exp } = JSON.parse(raw);
        if (Date.now() > exp) { localStorage.removeItem(LS_KEY); return; }
        setWaiting({ sessionId, botUrl });
        poll(sessionId);
      } catch(e) { localStorage.removeItem(LS_KEY); }
    };
    check();
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    return () => {
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
      clearInterval(pollRef.current);
    };
  }, []);

  const poll = (sessionId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/auth/telegram-poll/${sessionId}`);
        if (data.status === 'done') {
          clearInterval(pollRef.current);
          localStorage.removeItem(LS_KEY);
          localStorage.setItem('fitapp-token', data.token);
          window.location.replace('/dashboard');
        }
        // 'pending' и 'expired' — продолжаем ждать, не сбрасываем стейт
      } catch(e) { /* silent */ }
    }, 2000);
  };

  const startTgAuth = async () => {
    setError('');
    const sessionId = genId();
    const botUrl = `https://t.me/MolShape_bot?start=session_${sessionId}`;
    try {
      // Сначала регистрируем сессию, ПОТОМ показываем ожидание
      await api.post('/auth/telegram-start', { session_id: sessionId });
      const exp = Date.now() + 5 * 60 * 1000;
      localStorage.setItem(LS_KEY, JSON.stringify({ sessionId, botUrl, exp }));
      setWaiting({ sessionId, botUrl });
      poll(sessionId);
    } catch(e) {
      setError('Ошибка соединения. Попробуй снова.');
    }
  };

  const cancelTg = () => {
    clearInterval(pollRef.current);
    localStorage.removeItem(LS_KEY);
    setWaiting(null);
  };

  const submitForgot = async (e) => {
    e.preventDefault(); setFError(''); setFLoading(true);
    try {
      await api.post('/auth/forgot-password', { username: fLogin.trim() });
      setFSent(true);
    } catch(err) {
      setFError(err.response?.data?.error || 'Ошибка. Проверь логин или обратись к тренеру.');
    } finally { setFLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setError(''); setBlocked(false); setLoading(true);
    try { await login(form); navigate('/dashboard'); }
    catch (err) {
      if (err.message?.includes('Доступ ограничен')) setBlocked(true);
      else setError(err.message);
    } finally { setLoading(false); }
  };

  if (blocked) return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'linear-gradient(135deg,#0E0B18,#1A0030)',padding:'1rem'}}>
      <style>{CSS}</style>
      <div style={{textAlign:'center',color:'#fff',maxWidth:340}}>
        <div style={{fontSize:'3.5rem',marginBottom:'1rem'}}>🔒</div>
        <h2 style={{fontFamily:'system-ui',fontWeight:800,fontSize:'1.4rem',margin:'0 0 .5rem'}}>Доступ ограничен</h2>
        <p style={{color:'rgba(255,255,255,.5)',marginBottom:'1.5rem',fontSize:'.875rem'}}>Обратись к тренеру Алёне 😊</p>
        <a href="https://t.me/molchanova_fit" target="_blank" rel="noreferrer"
          style={{display:'inline-flex',gap:8,padding:'.8rem 2rem',borderRadius:'.75rem',background:'#C8005A',color:'#fff',textDecoration:'none',fontWeight:700}}>
          💬 @molchanova_fit
        </a>
        <br/>
        <button onClick={()=>setBlocked(false)} style={{marginTop:'1rem',background:'none',border:'none',color:'rgba(255,255,255,.4)',cursor:'pointer'}}>← Назад</button>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',position:'relative',overflow:'hidden',display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}}>
      <style>{CSS}</style>
      <div style={{position:'absolute',inset:0,background:'linear-gradient(-45deg,#0E0B18,#1A0032,#210A2E,#0A0A1C)',backgroundSize:'400% 400%',animation:'bg-move 12s ease infinite'}}/>
      <div style={{position:'absolute',top:'8%',left:'5%',width:480,height:480,borderRadius:'50%',background:'radial-gradient(circle,rgba(200,0,90,.35) 0%,transparent 68%)',filter:'blur(32px)',pointerEvents:'none'}}/>
      <div style={{position:'absolute',bottom:'5%',right:'5%',width:400,height:400,borderRadius:'50%',background:'radial-gradient(circle,rgba(0,207,219,.28) 0%,transparent 68%)',filter:'blur(40px)',pointerEvents:'none'}}/>

      <div style={{position:'relative',zIndex:10,width:'100%',maxWidth:400}}>
        <div style={{textAlign:'center',marginBottom:'1.75rem',animation:'slide-d .6s cubic-bezier(.16,1,.3,1) both'}}>
          <div className="ms-glow" style={{display:'inline-flex',width:76,height:76,borderRadius:22,background:'linear-gradient(135deg,#C8005A,#8B003D)',alignItems:'center',justifyContent:'center',marginBottom:14}}>
            <span style={{color:'#fff',fontFamily:'system-ui',fontWeight:900,fontSize:28,letterSpacing:-1}}>MS</span>
          </div>
          <h1 className="ms-logo" style={{fontFamily:'system-ui',fontSize:'2.5rem',fontWeight:900,letterSpacing:-2,lineHeight:1,margin:0}}>MolShape</h1>
          <p style={{color:'rgba(255,255,255,.4)',fontSize:'.875rem',marginTop:6}}>Твой прогресс. Твоя форма.</p>
        </div>

        <div style={{background:'rgba(255,255,255,.07)',backdropFilter:'blur(28px)',WebkitBackdropFilter:'blur(28px)',border:'1.5px solid rgba(255,255,255,.13)',borderRadius:24,padding:28,boxShadow:'0 30px 60px rgba(0,0,0,.55)',animation:'slide-u .6s cubic-bezier(.16,1,.3,1) .08s both'}}>

          {waiting ? (
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:'2.5rem',marginBottom:12,animation:'pulse 1.5s infinite'}}>✈️</div>
              <h3 style={{color:'#fff',fontWeight:700,fontSize:'1.05rem',margin:'0 0 8px'}}>Жди подтверждения</h3>
              <p style={{color:'rgba(255,255,255,.5)',fontSize:'.82rem',margin:'0 0 20px',lineHeight:1.65}}>
                1. Нажми кнопку ниже — откроется бот<br/>
                2. Нажми <strong style={{color:'#fff'}}>Start / Запустить</strong><br/>
                3. Вернись сюда — войдёшь автоматически
              </p>
              <a href={waiting.botUrl} target="_blank" rel="noreferrer" style={{
                display:'block',padding:'.9rem',borderRadius:14,marginBottom:12,
                background:'rgba(0,207,219,.15)',border:'1.5px solid rgba(0,207,219,.4)',
                color:'#00CFDB',fontSize:'.95rem',fontWeight:700,textDecoration:'none',
              }}>
                ✈️ Открыть @MolShape_bot
              </a>
              <div style={{display:'flex',alignItems:'center',gap:8,justifyContent:'center',marginBottom:14}}>
                <span style={{width:8,height:8,borderRadius:'50%',background:'#22C55E',animation:'pulse 1s infinite',display:'inline-block'}}/>
                <span style={{color:'rgba(255,255,255,.35)',fontSize:'.75rem'}}>Проверяю каждые 2 секунды...</span>
              </div>
              <button onClick={cancelTg} style={{background:'none',border:'1px solid rgba(255,255,255,.15)',borderRadius:10,padding:'.6rem 1.4rem',color:'rgba(255,255,255,.4)',cursor:'pointer',fontSize:'.78rem'}}>
                Отмена
              </button>
            </div>
          ) : (
            <>
              <h2 style={{color:'#fff',fontFamily:'system-ui',fontWeight:700,fontSize:'1.2rem',margin:'0 0 1.25rem'}}>Вход</h2>
              {error && <div style={{background:'rgba(239,68,68,.15)',border:'1px solid rgba(239,68,68,.3)',borderRadius:12,padding:'10px 14px',marginBottom:14,color:'#fca5a5',fontSize:'.875rem'}}>⚠️ {error}</div>}

              <form onSubmit={submit} style={{display:'flex',flexDirection:'column',gap:12}}>
                <div>
                  <label style={{display:'block',color:'rgba(255,255,255,.45)',fontSize:'.72rem',fontWeight:500,marginBottom:5}}>Логин</label>
                  <input className="ms-inp" placeholder="username" autoCapitalize="off"
                    value={form.username} onChange={e=>setForm(f=>({...f,username:e.target.value}))} required/>
                </div>
                <div>
                  <label style={{display:'block',color:'rgba(255,255,255,.45)',fontSize:'.72rem',fontWeight:500,marginBottom:5}}>Пароль</label>
                  <div style={{position:'relative'}}>
                    <input className="ms-inp" type={showPwd?'text':'password'} placeholder="••••••••"
                      style={{paddingRight:'2.75rem'}}
                      value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} required/>
                    <button type="button" onClick={()=>setShowPwd(s=>!s)} style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'rgba(255,255,255,.4)',fontSize:16}}>
                      {showPwd?'🙈':'👁'}
                    </button>
                  </div>
                </div>
                <div style={{textAlign:'right',margin:'-4px 0 6px'}}>
                <button type="button" onClick={()=>{setForgot(true);setFSent(false);setFError('');setFLogin('');}}
                  style={{background:'none',border:'none',color:'rgba(255,255,255,.35)',cursor:'pointer',fontSize:'.75rem',padding:0,textDecoration:'underline',textUnderlineOffset:2}}>
                  Забыл пароль?
                </button>
              </div>
              <button type="submit" className="ms-btn" disabled={loading} style={{marginTop:0}}>
                  {loading
                    ? <span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                        <span style={{width:16,height:16,border:'2.5px solid rgba(255,255,255,.3)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block'}}/>Вхожу...
                      </span>
                    : 'Войти'}
                </button>
              </form>

              <div style={{display:'flex',alignItems:'center',gap:10,margin:'16px 0'}}>
                <div style={{flex:1,height:1,background:'rgba(255,255,255,.1)'}}/>
                <span style={{color:'rgba(255,255,255,.3)',fontSize:'.75rem'}}>или</span>
                <div style={{flex:1,height:1,background:'rgba(255,255,255,.1)'}}/>
              </div>

              <button onClick={startTgAuth} disabled={loading} style={{
                width:'100%',padding:'.8rem',borderRadius:'.75rem',
                background:'rgba(0,207,219,.12)',border:'1.5px solid rgba(0,207,219,.3)',
                color:'#00CFDB',fontSize:'.875rem',fontWeight:600,cursor:'pointer',
                display:'flex',alignItems:'center',justifyContent:'center',gap:8,
              }}>
                ✈️ Войти через Telegram
              </button>

              <p style={{textAlign:'center',fontSize:'.78rem',color:'rgba(255,255,255,.28)',marginTop:16,marginBottom:0}}>
                Нет аккаунта?{' '}
                <Link to="/register" style={{color:'#FF3D90',textDecoration:'none',fontWeight:600}}>Зарегистрироваться</Link>
              </p>
            </>
          )}
        </div>
      </div>
      {/* Forgot password modal */}
      {forgot && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.75)',backdropFilter:'blur(6px)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}}>
          <div style={{background:'#1A0B2E',border:'1.5px solid rgba(255,255,255,.12)',borderRadius:20,padding:28,maxWidth:360,width:'100%',boxShadow:'0 30px 60px rgba(0,0,0,.6)'}}>
            <div style={{textAlign:'center',marginBottom:16}}>
              <div style={{fontSize:'2.5rem',marginBottom:8}}>🔑</div>
              <h3 style={{color:'#fff',fontFamily:'system-ui',fontWeight:700,fontSize:'1.1rem',margin:'0 0 8px'}}>Забыл пароль?</h3>
              <p style={{color:'rgba(255,255,255,.45)',fontSize:'.85rem',lineHeight:1.65,margin:0}}>
                {fSent
                  ? 'Проверьте Telegram — мы отправили ссылку для установки нового пароля (действует 30 минут).'
                  : 'Введите логин — если к аккаунту привязан Telegram, пришлём туда ссылку для сброса пароля.'}
              </p>
            </div>

            {!fSent && (
              <form onSubmit={submitForgot} style={{marginBottom:14}}>
                <input
                  type="text"
                  placeholder="Логин"
                  value={fLogin}
                  onChange={e=>setFLogin(e.target.value)}
                  style={{width:'100%',padding:'.75rem 1rem',borderRadius:12,border:'1px solid rgba(255,255,255,.15)',background:'rgba(255,255,255,.06)',color:'#fff',fontSize:'.9rem',marginBottom:10,outline:'none',boxSizing:'border-box'}}
                />
                {fError && (
                  <p style={{color:'#EF4444',fontSize:'.78rem',margin:'0 0 10px',lineHeight:1.5}}>{fError}</p>
                )}
                <button type="submit" disabled={fLoading} style={{width:'100%',background:'#C8005A',border:'none',borderRadius:12,padding:'.7rem',color:'#fff',fontWeight:700,fontSize:'.85rem',cursor:'pointer'}}>
                  {fLoading ? 'Отправляю...' : 'Отправить ссылку в Telegram'}
                </button>
              </form>
            )}

            <div style={{background:'rgba(200,0,90,.1)',border:'1px solid rgba(200,0,90,.25)',borderRadius:12,padding:'14px',marginBottom:14,textAlign:'center'}}>
              <p style={{color:'rgba(255,255,255,.5)',fontSize:'.75rem',margin:'0 0 8px'}}>Telegram не привязан? Напишите тренеру</p>
              <p style={{color:'#fff',fontWeight:700,fontSize:'.95rem',margin:'0 0 10px'}}>Молчанова Алёна</p>
              <a href="https://t.me/molchanova_fit" target="_blank" rel="noreferrer" style={{
                display:'inline-flex',alignItems:'center',gap:6,
                padding:'.6rem 1.2rem',borderRadius:10,
                background:'rgba(0,207,219,.15)',border:'1px solid rgba(0,207,219,.3)',
                color:'#00CFDB',textDecoration:'none',fontSize:'.85rem',fontWeight:600,
              }}>
                ✈️ Написать в Telegram
              </a>
            </div>
            <button onClick={()=>{setForgot(false);setFSent(false);setFError('');setFLogin('');}} style={{width:'100%',background:'none',border:'1px solid rgba(255,255,255,.1)',borderRadius:10,padding:'.65rem',color:'rgba(255,255,255,.35)',cursor:'pointer',fontSize:'.78rem'}}>
              Закрыть
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
